import React, { useEffect, useMemo, useState } from 'react';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { money } from './rateEngine';
import { ACCOUNT_TYPES, accountIndex, trialBalance } from './ledger';
import { Kpi } from './shared';

const SOURCES = [
  ['all', 'All'],
  ['manifest', 'Manifests'],
  ['settlement', 'Settlements'],
  ['invoice', 'Invoices'],
];

const downloadCsv = (entries, idx, from, to) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = [['Date', 'Entry', 'Source', 'Reference', 'Memo', 'Account Code', 'Account Name', 'Debit', 'Credit']];
  entries.forEach((e) =>
    e.lines.forEach((l) => rows.push([e.date, e.id, e.source, e.ref, e.memo, idx[l.account]?.code, idx[l.account]?.name || l.account, l.debit.toFixed(2), l.credit.toFixed(2)]))
  );
  const blob = new Blob([rows.map((r) => r.map(esc).join(',')).join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `general_ledger_${from}_${to}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

/** Codes and names can be changed at any time; journal lines point at the stable account id. */
const ChartOfAccounts = ({ accounts, onSave }) => {
  const [draft, setDraft] = useState(accounts);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(accounts), [accounts]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(accounts);
  const codes = draft.map((a) => String(a.code).trim());
  const duplicate = codes.find((c, i) => c && codes.indexOf(c) !== i);
  const update = (id, patch) => setDraft((d) => d.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border">
        <p className="text-sm text-muted-foreground">
          Starter chart — renumber or rename freely. History follows each account because entries reference the account, not its code.
        </p>
        <div className="flex items-center gap-2">
          {duplicate && <span className="text-xs text-red-500">Code {duplicate} is used twice</span>}
          <Button
            id="coa-add"
            size="sm"
            variant="outline"
            onClick={() => setDraft((d) => [...d, { id: `acct_${Date.now()}`, code: '', name: 'New account', type: 'Expense' }])}
          >
            <i className="fas fa-plus mr-2"></i>Add account
          </Button>
          <Button id="coa-discard" size="sm" variant="outline" disabled={!dirty || saving} onClick={() => setDraft(accounts)}>
            Discard
          </Button>
          <Button id="coa-save" size="sm" disabled={!dirty || saving || !!duplicate} onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            Save
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[600px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-muted/80 backdrop-blur text-xs uppercase text-muted-foreground">
            <tr>
              <th className="text-left p-3 w-32">Code</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3 w-40">Type</th>
            </tr>
          </thead>
          <tbody>
            {[...draft]
              .sort((a, b) => String(a.code).localeCompare(String(b.code)))
              .map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <td className="p-2">
                    <Input id={`coa-code-${a.id}`} value={a.code} onChange={(e) => update(a.id, { code: e.target.value })} className="h-8 font-mono" />
                  </td>
                  <td className="p-2">
                    <Input id={`coa-name-${a.id}`} value={a.name} onChange={(e) => update(a.id, { name: e.target.value })} className="h-8" />
                  </td>
                  <td className="p-2">
                    <select
                      id={`coa-type-${a.id}`}
                      value={a.type}
                      onChange={(e) => update(a.id, { type: e.target.value })}
                      className="bg-background border border-border rounded text-sm h-8 px-2 w-full"
                    >
                      {ACCOUNT_TYPES.map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Screen 6 — General Ledger
 * Double-entry journal from posted manifests, settlements and invoices, a trial balance, and the chart of accounts.
 * Shows the server ledger when GET /api/accounting/gl/entries is live, otherwise a browser preview.
 */
const GeneralLedger = ({ localEntries, loadJournal, demo, accounts, onSaveAccounts }) => {
  const [from, setFrom] = useState(() => format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [to, setTo] = useState(() => format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [source, setSource] = useState('all');
  const [view, setView] = useState('journal');
  const [serverEntries, setServerEntries] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadJournal(from, to).then((entries) => !cancelled && setServerEntries(entries));
    return () => {
      cancelled = true;
    };
  }, [from, to, loadJournal, demo]);

  const idx = useMemo(() => accountIndex(accounts), [accounts]);
  const live = !!serverEntries;
  const entries = useMemo(
    () => (serverEntries || localEntries).filter((e) => e.date >= from && e.date <= to && (source === 'all' || e.source === source)),
    [serverEntries, localEntries, from, to, source]
  );
  const tb = useMemo(() => trialBalance(entries, accounts), [entries, accounts]);
  const totalDr = tb.reduce((s, r) => s + r.debit, 0);
  const totalCr = tb.reduce((s, r) => s + r.credit, 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.01;
  const bal = (id) => tb.find((r) => r.id === id)?.balance || 0;
  const revenue = -tb.filter((r) => r.type === 'Revenue').reduce((s, r) => s + r.balance, 0);

  return (
    <div className="space-y-6" id="general-ledger-screen">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            General Ledger
            <Badge variant="outline" className={live ? 'border-emerald-500/40 text-emerald-500' : 'border-amber-500/40 text-amber-500'}>
              {live ? 'Server ledger' : 'Preview (computed in browser)'}
            </Badge>
          </h2>
          <p className="text-sm text-muted-foreground">Journal entries generated when manifests are posted, driver pay is approved/paid and invoices are issued.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input id="gl-from" type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} className="w-[150px]" />
          <span className="text-muted-foreground">→</span>
          <Input id="gl-to" type="date" value={to} onChange={(e) => e.target.value && setTo(e.target.value)} className="w-[150px]" />
          <Button id="gl-export-csv" variant="outline" disabled={!entries.length} onClick={() => downloadCsv(entries, idx, from, to)}>
            <i className="fas fa-file-csv mr-2"></i>Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi label="Revenue" value={money(revenue)} sub="all revenue accounts" icon="fa-chart-line" accent="text-emerald-500" />
        <Kpi label="Driver Pay" value={money(bal('driver_pay'))} icon="fa-truck" />
        <Kpi label="Sales Tax Collected" value={money(-(bal('gst_payable') + bal('qst_payable')))} sub="GST + QST payable" icon="fa-landmark" />
        <Kpi
          label="Driver Pay Owing"
          value={money(-bal('driver_payable'))}
          sub={balanced ? `Books balance · ${entries.length} entries` : `⚠ Out of balance by ${money(totalDr - totalCr)}`}
          icon="fa-balance-scale"
          accent={balanced ? 'text-foreground' : 'text-red-500'}
        />
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2">
              {[
                ['journal', 'Journal'],
                ['trial-balance', 'Trial Balance'],
                ['accounts', 'Chart of Accounts'],
              ].map(([key, label]) => (
                <Button key={key} id={`gl-view-${key}`} size="sm" variant={view === key ? 'default' : 'outline'} onClick={() => setView(key)}>
                  {label}
                </Button>
              ))}
            </div>
            {view !== 'accounts' && (
              <div className="flex gap-2">
                {SOURCES.map(([key, label]) => (
                  <Button key={key} id={`gl-source-${key}`} size="sm" variant={source === key ? 'secondary' : 'ghost'} onClick={() => setSource(key)}>
                    {label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {view === 'accounts' ? (
            <ChartOfAccounts accounts={accounts} onSave={onSaveAccounts} />
          ) : view === 'journal' ? (
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Entry / Account</th>
                    <th className="text-right p-3">Debit</th>
                    <th className="text-right p-3">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <React.Fragment key={e.id}>
                      <tr className="border-t border-border bg-muted/20">
                        <td className="p-3 align-top whitespace-nowrap">{e.date}</td>
                        <td className="p-3" colSpan={3}>
                          <span className="font-mono text-xs mr-2">{e.id}</span>
                          <Badge variant="outline" className="text-[10px] mr-2">
                            {e.source}
                          </Badge>
                          <span className="text-muted-foreground">{e.memo}</span>
                        </td>
                      </tr>
                      {e.lines.map((l, i) => (
                        <tr key={i}>
                          <td></td>
                          <td className={`p-1.5 ${l.credit ? 'pl-12' : 'pl-6'}`}>
                            <span className="font-mono text-xs text-muted-foreground mr-2">{idx[l.account]?.code || '?'}</span>
                            {idx[l.account]?.name || l.account}
                          </td>
                          <td className="p-1.5 pr-3 text-right">{l.debit ? money(l.debit) : ''}</td>
                          <td className="p-1.5 pr-3 text-right">{l.credit ? money(l.credit) : ''}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {entries.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-8 text-center text-muted-foreground">
                        No journal entries in this period. Entries are created when a manifest is posted.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/80 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Account</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-right p-3">Debit</th>
                  <th className="text-right p-3">Credit</th>
                  <th className="text-right p-3">Balance</th>
                </tr>
              </thead>
              <tbody>
                {tb.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="p-3">
                      <span className="font-mono text-xs text-muted-foreground mr-2">{r.code}</span>
                      {r.name}
                    </td>
                    <td className="p-3 text-muted-foreground">{r.type}</td>
                    <td className="p-3 text-right">{money(r.debit)}</td>
                    <td className="p-3 text-right">{money(r.credit)}</td>
                    <td className="p-3 text-right font-semibold">
                      {money(Math.abs(r.balance))} {r.balance >= 0 ? 'Dr' : 'Cr'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-bold">
                  <td className="p-3" colSpan={2}>
                    Totals {balanced ? <i className="fas fa-check-circle text-emerald-500 ml-2"></i> : <i className="fas fa-exclamation-triangle text-red-500 ml-2"></i>}
                  </td>
                  <td className="p-3 text-right">{money(totalDr)}</td>
                  <td className="p-3 text-right">{money(totalCr)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GeneralLedger;
